if (!customElements.get("m-icon-box")) {
  class MIconbox extends HTMLElement {
    constructor() {
      super();

      this.enableSlider = this.dataset.enableSlider === "true";
      this.mobileFade = this.dataset.mobileFade === "true";
      this.items = this.dataset.items;
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.enableAutoplay = this.dataset.enableAutoplay === "true";
      this.autoplayDelay = (parseInt(this.dataset.autoplayDelay, 10) || 3) * 1000;
      this.slideContainer = this.querySelector(".swiper-container");
      this.mobileMedia = window.matchMedia("(max-width: 767px)");
      this.slider = null;
      this.lastActive = 0;
      this._onBreakpoint = () => this.handleBreakpoint();
    }

    connectedCallback() {
      if (this.enableSlider || this.mobileFade) {
        this.initCarousel();
        this.mobileMedia.addEventListener("change", this._onBreakpoint);
      }
      document.addEventListener("shopify:block:select", (e) => this.onBlockSelect(e));
    }

    disconnectedCallback() {
      this.destroySlider();
      this.mobileMedia.removeEventListener("change", this._onBreakpoint);
    }

    isMobile() {
      return this.mobileMedia.matches;
    }

    shouldRunSwiper() {
      if (this.enableSlider) return true;
      return this.mobileFade && this.isMobile();
    }

    getAutoplayConfig() {
      if (!this.enableAutoplay) return false;
      return {
        delay: this.autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    getPaginationConfig() {
      const el = this.querySelector(".swiper-pagination");
      if (!this.showPagination || !el) return false;
      return { el, clickable: true, dynamicBullets: false, dynamicMainBullets: 1 };
    }

    getMobileConfig(slideItemsLength) {
      const config = {
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 0,
        loop: slideItemsLength > 1,
        speed: 500,
        allowTouchMove: slideItemsLength > 1,
        navigation: false,
        pagination: this.getPaginationConfig(),
        autoplay: this.getAutoplayConfig(),
      };

      if (this.mobileFade) {
        config.effect = "fade";
        config.fadeEffect = { crossFade: true };
      }

      return config;
    }

    getDesktopConfig(slideItemsLength) {
      const items = parseInt(this.items, 10) || 4;
      return {
        slidesPerView: Math.min(items, slideItemsLength),
        spaceBetween: 0,
        loop: slideItemsLength > items,
        speed: 400,
        navigation: this.showNavigation,
        pagination: this.getPaginationConfig(),
        autoplay: this.getAutoplayConfig(),
        breakpoints: {
          480: { slidesPerView: Math.min(2, slideItemsLength) },
          768: { slidesPerView: Math.min(items - 1, slideItemsLength) },
          1280: { slidesPerView: Math.min(items, slideItemsLength) },
        },
      };
    }

    initCarousel() {
      if (!this.slideContainer) return;
      if (!this.shouldRunSwiper()) {
        this.destroySlider();
        return;
      }

      const wrapper = this.querySelector(".swiper-wrapper");
      if (!wrapper) return;

      const slideItemsLength = wrapper.childElementCount;
      if (slideItemsLength < 1) return;

      const controlsContainer = this.querySelector(".m-icon-box__slider-controls");
      const prevButton = controlsContainer?.querySelector(".m-slider-controls__button-prev");
      const nextButton = controlsContainer?.querySelector(".m-slider-controls__button-next");
      const __this = this;

      const baseConfig = this.isMobile() ? this.getMobileConfig(slideItemsLength) : this.getDesktopConfig(slideItemsLength);

      this.destroySlider();

      this.slider = new MinimogLibs.Swiper(this.slideContainer, {
        ...baseConfig,
        on: {
          init(swiper) {
            swiper.slideToLoop(__this.lastActive, 0);
            __this.positionControls(controlsContainer, prevButton, nextButton);
            __this.toggleControlsVisibility(controlsContainer, slideItemsLength, swiper.params.slidesPerView);
          },
          slideChange(swiper) {
            __this.lastActive = swiper.realIndex;
          },
          breakpoint(swiper, breakpointParams) {
            __this.toggleControlsVisibility(controlsContainer, slideItemsLength, breakpointParams.slidesPerView);
          },
        },
      });

      if (this.slider && this.showNavigation) {
        prevButton?.addEventListener("click", () => this.slider.slidePrev());
        nextButton?.addEventListener("click", () => this.slider.slideNext());
      }
    }

    handleBreakpoint() {
      this.initCarousel();
    }

    destroySlider() {
      if (this.slider) {
        this.slider.destroy(true, true);
        this.slider = null;
      }
    }

    positionControls(controlsContainer, prevButton, nextButton) {
      if (!controlsContainer) return;
      setTimeout(() => {
        const firstItem = this.querySelector(".m-image") || this.querySelector(".m-placeholder-svg");
        if (firstItem && this.enableSlider) {
          const itemHeight = firstItem.clientHeight;
          controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight, 10) / 2 + "px");
          prevButton?.classList.remove("m:hidden");
          nextButton?.classList.remove("m:hidden");
        }
      }, 200);
    }

    toggleControlsVisibility(controlsContainer, slideItemsLength, slidesPerView) {
      if (!controlsContainer) return;
      const perView = typeof slidesPerView === "number" ? slidesPerView : 1;
      if (slideItemsLength > perView) {
        controlsContainer.classList.remove("m:hidden");
        if (this.slider) this.slider.allowTouchMove = true;
      } else {
        controlsContainer.classList.add("m:hidden");
        if (this.slider) this.slider.allowTouchMove = false;
      }
    }

    onBlockSelect(e) {
      const block = e.target.closest("[data-slide]");
      if (!block || !this.contains(block)) return;
      const index = Number(block.dataset.slide);
      this.lastActive = index;
      if (this.slider) {
        this.slider.slideToLoop(index);
      }
    }
  }

  customElements.define("m-icon-box", MIconbox);
}
